import React from 'react';
import { useTranslateRegions } from '@hero/api-hooks';
import type { Language } from '@hero/schema';
import { cn } from '@hero/web-lib';

export interface RegionNameProps {
  /**
   * The region's canonical `customCode` (e.g. `'riyadh'`, `'jeddah_islamic_port'`).
   * Orders / agreements / routes / invoices store only `customCode` — display
   * names are resolved here at render time.
   */
  customCode: string | undefined;
  /**
   * Force a specific language. Defaults to the current `i18next` language
   * (`useTranslation().i18n.language`).
   */
  lang?: Language;
  /**
   * Rendered when `customCode` is missing or temporarily resolves to itself
   * (cache miss before L2 settles). Useful for placeholders / skeletons.
   * Default: render the raw `customCode` as-is.
   */
  fallback?: React.ReactNode;
  /** Extra Tailwind classes. */
  className?: string;
  /** Optional wrapping element (default `'span'`). Useful inside table cells / form labels. */
  as?: keyof React.JSX.IntrinsicElements;
}

/**
 * Shared component for rendering a region's display name in the viewer's
 * language. Hides the 5-layer caching strategy
 * (`docs/regions/01-translation-and-cache.md`) behind a single, tree-shake-
 * friendly API.
 *
 * **Why a dedicated component?**
 *
 * - Lets us swap the resolution strategy without touching dozens of call
 *   sites — e.g. when we add an offline-first IndexedDB layer or move to a
 *   GraphQL fragment, only this component changes.
 * - Auto-handles the missing-code case (renders the raw `customCode`),
 *   so order-detail / invoice / route rendering stays robust against new
 *   regions the client hasn't seen yet.
 * - Keeps `Order` / `Agreement` / `Invoice` docs lean — no denormalized
 *   `displayName` snapshots that drift after a Super-Admin rename.
 *
 * @example
 *   <RegionName customCode={order.contracts[0].route.start.customCode} />
 *   <RegionName customCode="jeddah_islamic_port" lang="ar" />
 *   <RegionName customCode={undefined} fallback="—" />
 */
export const RegionName: React.FC<RegionNameProps> = ({
  customCode,
  lang,
  fallback,
  className,
  as: Tag = 'span',
}) => {
  // Batched lookup — calling this component many times in one render still
  // emits a single network round-trip because TanStack Query merges identical
  // `queryKey`s.
  const { display } = useTranslateRegions(customCode ? [customCode] : [], lang);

  if (!customCode) {
    return <Tag className={className}>{fallback ?? null}</Tag>;
  }

  const value = display(customCode);
  // Prefer fallback (snapshot displayName) whenever translation resolves to the
  // raw slug — during load AND after a cache miss. Matches RegionLane / mobile
  // FilterModal so pricing selectors don't flash "Jeddah" → "jeddah".
  const label =
    value && value !== customCode
      ? value
      : fallback !== undefined
        ? fallback
        : value;
  return <Tag className={className}>{label}</Tag>;
};
